import React from 'react';
import { MultiSelect } from 'primereact/multiselect';

interface MultiSelectOption {
  label: string;
  value: string;
  [key: string]: any; // Allow additional properties like courseId, batchTime, etc.
}

interface GlobalMultiSelectFieldProps {
  label?: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  icon?: string;
  fullWidth?: boolean;
  display?: 'comma' | 'chip';
  maxSelectedLabels?: number;
  emptyMessage?: string;
  filterPlaceholder?: string;
  showClear?: boolean;
  selectAll?: boolean;
}

export const GlobalMultiSelectField: React.FC<GlobalMultiSelectFieldProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select options',
  required = false,
  disabled = false,
  error = '',
  icon,
  fullWidth = true,
  display = 'chip',
  maxSelectedLabels = 3,
  emptyMessage = 'No options available',
  filterPlaceholder = 'Search...',
  showClear = true,
  selectAll = true,
}) => {
  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <label style={{
          display: 'block',
          fontSize: '0.85rem',
          fontWeight: 600,
          color: error ? '#dc3545' : '#0f3d3e',
          marginBottom: '6px',
          letterSpacing: '0.2px'
        }}>
          {label} {required && <span style={{ color: '#dc3545' }}>*</span>}
        </label>
      )}
      
      <div style={{ position: 'relative' }}>
        {icon && (
          <i 
            className={icon} 
            style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#0f3d3e',
              fontSize: '1rem',
              pointerEvents: 'none',
              zIndex: 1,
              opacity: 0.5
            }}
          ></i>
        )}
        
        <MultiSelect
          value={value}
          onChange={(e) => onChange(e.value)}
          options={options}
          placeholder={placeholder}
          disabled={disabled || options.length === 0}
          display={display}
          maxSelectedLabels={maxSelectedLabels}
          emptyMessage={emptyMessage}
          filter
          filterPlaceholder={filterPlaceholder}
          showClear={showClear}
          selectAll={selectAll}
          style={{
            width: '100%',
          }}
          panelStyle={{
            fontSize: '0.85rem',
            borderRadius: '10px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
          }}
          pt={{
            root: {
              style: {
                border: `1px solid ${error ? '#dc3545' : '#e5e7eb'}`,
                borderRadius: '10px',
                padding: icon ? '0px 14px 0px 42px' : '12px 14px',
                fontSize: '0.9rem',
                minHeight: '43px',
                background: disabled || options.length === 0 ? '#f8f9fa' : 'white',
                transition: 'all 0.2s ease',
                fontWeight: 500
              },
              className: 'global-multiselect-field'
            },
            header: {
              style: {
                padding: '12px',
                borderBottom: '1px solid #e5e7eb',
                background: '#f8f9fa'
              }
            },
            filterContainer: {
              style: {
                padding: '0'
              }
            },
            filterInput: {
              style: {
                padding: '10px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '0.85rem',
                width: '100%'
              }
            },
            wrapper: {
              style: {
                maxHeight: '300px'
              }
            },
            item: {
              style: {
                padding: '10px 12px',
                fontSize: '0.85rem',
                transition: 'all 0.15s ease'
              }
            },
            token: {
              style: {
                padding: '4px 10px',
                background: '#d4f17e',
                color: '#0f3d3e',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: 500,
                margin: '2px'
              }
            },
            removeTokenIcon: {
              style: {
                color: '#0f3d3e',
                marginLeft: '6px',
                fontSize: '0.875rem'
              }
            }
          }}
        />
      </div>
      
      {error && (
        <span style={{
          display: 'block',
          marginTop: '6px',
          fontSize: '0.75rem',
          color: '#dc3545',
          fontWeight: 500
        }}>
          {error}
        </span>
      )}

      {/* Info text for disabled state */}
      {(disabled || options.length === 0) && !error && (
        <span style={{
          display: 'block',
          marginTop: '6px',
          fontSize: '0.75rem',
          color: '#6c757d',
          fontWeight: 400,
          fontStyle: 'italic'
        }}>
          {options.length === 0 ? emptyMessage : 'Field is currently disabled'}
        </span>
      )}

      <style>{`
        .global-multiselect-field:not(.p-disabled):hover {
          border-color: #1a5555 !important;
        }

        .global-multiselect-field.p-focus {
          outline: 0;
          border-color: #0f3d3e !important;
          box-shadow: 0 0 0 3px rgba(15, 61, 62, 0.1) !important;
        }

        .global-multiselect-field.p-disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .global-multiselect-field .p-placeholder {
          color: #999;
          opacity: 0.7;
        }

        /* Checkbox styling - Updated to match design theme */
        .p-checkbox .p-checkbox-box {
          border: 2px solid #e5e7eb;
          border-radius: 4px;
          transition: all 0.2s ease;
          width: 1.125rem;
          height: 1.125rem;
        }

        .p-checkbox .p-checkbox-box .p-checkbox-icon {
          font-size: 0.75rem;
          color: white;
        }

        .p-checkbox .p-checkbox-box.p-highlight {
          background: #0f3d3e !important;
          border-color: #0f3d3e !important;
        }

        /* Target highlighted checkbox */
        .p-checkbox.p-highlight .p-checkbox-box {
          background: #0f3d3e !important;
          border-color: #0f3d3e !important;
        }

        /* Target checkbox hover when highlighted */
        .p-checkbox:not(.p-disabled):has(.p-checkbox-input:hover).p-highlight .p-checkbox-box {
          background: #1a5555 !important;
          border-color: #1a5555 !important;
        }

        .p-checkbox .p-checkbox-box:hover {
          border-color: #1a5555;
        }
        
        .p-checkbox:not(.p-checkbox-disabled) .p-checkbox-box.p-focus {
          outline: 0;
          border-color: #0f3d3e !important;
          box-shadow: 0 0 0 2px rgba(15, 61, 62, 0.15) !important;
        }

        /* Dropdown item hover */
        .p-multiselect-panel .p-multiselect-item:not(.p-disabled):hover {
          background: rgba(15, 61, 62, 0.05);
        }

        .p-multiselect-panel .p-multiselect-item.p-highlight {
          background: rgba(15, 61, 62, 0.1);
          color: #0f3d3e;
        }

        /* Select All checkbox */
        .p-multiselect-header .p-checkbox-box.p-highlight {
          background: #0f3d3e !important;
          border-color: #0f3d3e !important;
        }

        /* Clear icon */
        .global-multiselect-field .p-multiselect-clear-icon {
          color: #0f3d3e;
          opacity: 0.5;
          transition: opacity 0.2s ease;
          font-size: 1rem;
        }

        .global-multiselect-field .p-multiselect-clear-icon:hover {
          opacity: 1;
        }

        /* Dropdown trigger icon */
        .global-multiselect-field .p-multiselect-trigger {
          color: #0f3d3e;
          opacity: 0.5;
          width: 2rem;
        }

        .global-multiselect-field .p-multiselect-trigger .p-icon {
          font-size: 1rem;
        }

        /* Empty message */
        .p-multiselect-panel .p-multiselect-empty-message {
          padding: 16px;
          text-align: center;
          color: #6c757d;
          font-size: 0.85rem;
          font-style: italic;
        }

        /* Filter input focus */
        .p-multiselect-filter:focus {
          outline: 0;
          border-color: #0f3d3e !important;
          box-shadow: 0 0 0 2px rgba(15, 61, 62, 0.1) !important;
        }
      `}</style>
    </div>
  );
};